import { PaymentViewLayout } from 'components/Layout/PaymentViewLayout';
import { useAuthorizePlatformEntityBasisTheorySessionMutation } from 'hooks/internalApi';
import { useInternalApiContext } from 'hooks/useInternalApiContext';
import React from 'react';
import { FragmentEnv } from 'utils/env';
import {
  BasisTheoryProvider,
  useBasisTheory,
} from '@basis-theory/react-elements';
import { useQuery } from '@tanstack/react-query';

type EntityPiiPageContentProps = {
  workspaceId: string;
  onBack: () => void;
};

const EntityPiiPageContent = ({
  workspaceId,
  onBack,
}: EntityPiiPageContentProps) => {
  const { bt } = useBasisTheory();
  const { context: internalApiCtx } = useInternalApiContext();
  const [, authorizeSession] =
    useAuthorizePlatformEntityBasisTheorySessionMutation();

  const { data: sessionKey, error } = useQuery({
    queryKey: ['bt-session', workspaceId],
    enabled: bt != null,
    retry: false,
    queryFn: async () => {
      const { sessionKey: key, nonce } = await bt!.sessions.create();

      const result = await authorizeSession(
        { workspaceId, nonce },
        internalApiCtx
      );

      if (result.error) {
        throw new Error(result.error.message);
      }

      const data = result.data?.authorizePlatformEntityBasisTheorySession;
      if (!data) {
        throw new Error('No response from server');
      }

      if (
        data.__typename === 'BadRequestError' ||
        data.__typename === 'InternalError'
      ) {
        throw new Error(data.message);
      }

      if (!data.success) {
        throw new Error('Session authorization failed');
      }

      return key;
    },
  });

  return (
    <PaymentViewLayout
      title="Entity PII"
      onBack={onBack}
      backTitle="Payment Settings"
    >
      <div className="flex flex-col gap-y-f2 p-f2">
        {!bt && (
          <p className="text-main-500">Initializing Basis Theory session...</p>
        )}
        {bt && !sessionKey && !error && (
          <p className="text-main-500">Creating and authorizing session...</p>
        )}
        {error && (
          <div className="flex flex-col gap-y-f1">
            <p className="text-main text-md">Session authorization failed</p>
            <p className="text-main-500 text-md">{error.message}</p>
          </div>
        )}
        {sessionKey && (
          <div className="flex flex-col gap-y-f1">
            <p className="text-positive font-medium">Session authorized</p>
            <p className="text-main-500 text-sm font-mono break-all">
              Session key: {sessionKey}
            </p>
          </div>
        )}
      </div>
    </PaymentViewLayout>
  );
};

type EntityPiiPageProps = {
  workspaceId: string;
  onBack: () => void;
};

const EntityPiiPageProvider = ({ workspaceId, onBack }: EntityPiiPageProps) => {
  const dashboardApiKey = FragmentEnv.basisTheory?.dashboardApiKey;
  const { bt } = useBasisTheory(dashboardApiKey);

  if (!dashboardApiKey) {
    return (
      <PaymentViewLayout
        title="Entity PII"
        onBack={onBack}
        backTitle="Payment Settings"
      >
        <div className="flex flex-col gap-y-f2 p-f2">
          <p className="text-main-500">
            Basis Theory is not configured for this environment.
          </p>
        </div>
      </PaymentViewLayout>
    );
  }

  return (
    <BasisTheoryProvider bt={bt}>
      <EntityPiiPageContent workspaceId={workspaceId} onBack={onBack} />
    </BasisTheoryProvider>
  );
};

export const EntityPiiPage = EntityPiiPageProvider;
